import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { StatusChip } from '../components/app/StatusChip';
import { SuggestionBadge } from '../components/app/SuggestionBadge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

export default function ApprovalsPage() {
  const { activePeriod } = usePeriod();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen]   = useState(false);
  const [comment, setComment] = useState('');
  const [acting, setActing]   = useState(false);
  const [periodFilter, setPeriodFilter] = useState(activePeriod?.id || '');
  const [periods, setPeriods] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodFilter) params.period_id = periodFilter;
      const res = await API.get('/approvals', { params });
      setItems(res.data.items || []);
    } catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    API.get('/periods').then(r => setPeriods(r.data)).catch(() => {});
    if (activePeriod) setPeriodFilter(activePeriod.id);
  }, [activePeriod?.id]);

  useEffect(() => { load(); }, [periodFilter]);

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const selectAll = () => setSelected(items.map(i => i.id));

  const doAction = async (action) => {
    setActing(true);
    try {
      const ids = selected.length > 0 ? selected : [selectedTxn?.id].filter(Boolean);
      if (ids.length === 0) { toast.error('No transactions selected'); return; }
      await API.post('/approvals/action', { transaction_ids: ids, action, comment });
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} ${ids.length} transaction(s)`);
      setApproveDialogOpen(false); setRejectDialogOpen(false);
      setComment(''); setSelected([]); setPanelOpen(false); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setActing(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold">Approvals</h1>
        <div className="flex items-center gap-2">
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="h-8 text-xs w-40">
              <SelectValue placeholder="All periods" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="">All periods</SelectItem>
              {periods.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {selected.length > 0 && (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setApproveDialogOpen(true)} data-testid="approvals-bulk-approve-button">
                <CheckCircle2 size={13} className="mr-1" /> Approve ({selected.length})
              </Button>
              <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => setRejectDialogOpen(true)} data-testid="approvals-reject-button">
                <XCircle size={13} className="mr-1" /> Reject
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="approvals-queue-table">
          <thead><tr>
            <th className="w-8"><input type="checkbox" onChange={e => e.target.checked ? selectAll() : setSelected([])} /></th>
            <th>Status</th><th>Date</th><th>Merchant</th>
            <th className="text-right">Amount</th>
            <th>Cardholder</th><th>Category</th><th>Cost Center</th>
            <th>Submitted By</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-slate-400">No transactions pending approval</td></tr>
            ) : items.map(t => (
              <tr key={t.id} className="cursor-pointer" onClick={() => { setSelectedTxn(t); setPanelOpen(true); }}>
                <td onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} />
                </td>
                <td><StatusChip status={t.status} size="xs" /></td>
                <td className="text-xs">{t.date}</td>
                <td className="text-sm font-medium max-w-48 truncate">{t.description_raw}</td>
                <td className="text-right tabular-nums font-semibold text-sm">{t.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                <td className="text-xs">{t.cardholder_name || '—'}</td>
                <td className="text-xs">{t.category_name || <span className="text-orange-400">Uncoded</span>}</td>
                <td className="text-xs">{t.cost_center_name || '—'}</td>
                <td className="text-xs text-slate-500">{t.submitted_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent side="right" className="w-[380px] bg-white">
          <SheetHeader><SheetTitle className="text-sm">Review Transaction</SheetTitle></SheetHeader>
          {selectedTxn && (
            <div className="space-y-4 pt-4 pb-20 overflow-y-auto">
              <div className="border rounded p-3 space-y-1">
                <div className="text-xs text-slate-500">{selectedTxn.date}</div>
                <div className="font-semibold">{selectedTxn.description_raw}</div>
                <div className="text-xl font-bold">{selectedTxn.amount?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })}</div>
                <div className="flex gap-2"><StatusChip status={selectedTxn.status} size="xs" /></div>
              </div>
              <div className="text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Category</span><span className="font-medium">{selectedTxn.category_name || 'Uncoded'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Cost Center</span><span className="font-medium">{selectedTxn.cost_center_name || '—'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GL Account</span><span className="font-mono">{selectedTxn.gl_account || '—'}</span></div>
                {selectedTxn.description && <div><span className="text-slate-500">Notes: </span>{selectedTxn.description}</div>}
              </div>
              <div>
                <label className="text-xs font-medium">Review Comment</label>
                <Textarea value={comment} onChange={e => setComment(e.target.value)} className="mt-1 text-sm h-16 resize-none" placeholder="Optional comment…" data-testid="approvals-comment-textarea" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => doAction('approve')} disabled={acting}>
                  {acting ? <RefreshCw size={13} className="animate-spin mr-1" /> : <CheckCircle2 size={13} className="mr-1" />} Approve
                </Button>
                <Button size="sm" variant="outline" className="flex-1 text-rose-600 border-rose-200" onClick={() => doAction('reject')} disabled={acting}>
                  <XCircle size={13} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk approve dialog */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {selected.length} Transaction(s)?</AlertDialogTitle>
            <AlertDialogDescription>This will approve all selected transactions.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Optional approval comment" className="text-sm h-16 resize-none" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doAction('approve')} className="bg-emerald-600" disabled={acting}>Approve All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk reject dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject {selected.length} Transaction(s)?</AlertDialogTitle>
            <AlertDialogDescription>Rejected transactions will return to the exceptions queue.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Rejection reason (required)" className="text-sm h-16 resize-none" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doAction('reject')} className="bg-rose-600" disabled={acting}>Reject All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
